import { useState } from "react";
import { api } from "../lib/api";

export type OfferFormValues = {
	title: string;
	vertical: string;
	payoutType: "cpa" | "cpl" | "revshare";
	payoutAmount: string;
	geos: string;
	minVolume: string;
};

export const EMPTY_OFFER: OfferFormValues = {
	title: "",
	vertical: "casino",
	payoutType: "cpa",
	payoutAmount: "50",
	geos: "DE,AT",
	minVolume: "1000",
};

type OfferTemplate = OfferFormValues & { id: string; label: string };

const OFFER_TEMPLATES: OfferTemplate[] = [
	{
		id: "dach-casino-cpa50",
		label: "DACH casino · $50 CPA",
		title: "DACH casino CPA",
		vertical: "casino",
		payoutType: "cpa",
		payoutAmount: "50",
		geos: "DE,AT,CH",
		minVolume: "1000",
	},
	{
		id: "uk-sports-cpa75",
		label: "UK sportsbook · $75 CPA",
		title: "UK sportsbook CPA",
		vertical: "sportsbook",
		payoutType: "cpa",
		payoutAmount: "75",
		geos: "GB",
		minVolume: "500",
	},
	{
		id: "nordic-poker-cpl15",
		label: "Nordic poker · $15 CPL",
		title: "Nordic poker CPL",
		vertical: "poker",
		payoutType: "cpl",
		payoutAmount: "15",
		geos: "SE,NO,FI,DK",
		minVolume: "300",
	},
	{
		id: "global-crypto-revshare30",
		label: "Global crypto · 30% revshare",
		title: "Global crypto revshare",
		vertical: "crypto",
		payoutType: "revshare",
		payoutAmount: "30",
		geos: "DE,AT,CH,GB,SE,NO,NL,PT,ES",
		minVolume: "200",
	},
];

export function offerPayload(v: OfferFormValues) {
	return {
		title: v.title,
		vertical: v.vertical,
		payoutType: v.payoutType,
		payoutAmount: Number.parseFloat(v.payoutAmount),
		payoutCurrency: "USD",
		constraints: {
			geos: v.geos
				.split(",")
				.map((g) => g.trim().toUpperCase())
				.filter(Boolean),
			verticals: [v.vertical],
			minMonthlyVolume: Number.parseInt(v.minVolume, 10) || 0,
		},
	};
}

export function OfferForm(props: {
	initial: OfferFormValues;
	submitLabel: string;
	onSubmit: (v: OfferFormValues) => Promise<void>;
	onCancel: () => void;
	showTemplates?: boolean;
	error?: string | null;
}) {
	const [v, setV] = useState<OfferFormValues>(props.initial);
	const [submitting, setSubmitting] = useState(false);
	const [localError, setLocalError] = useState<string | null>(null);
	const err = localError ?? props.error;

	function field<K extends keyof OfferFormValues>(k: K) {
		return (val: OfferFormValues[K]) => setV((prev) => ({ ...prev, [k]: val }));
	}

	async function submit() {
		setLocalError(null);
		setSubmitting(true);
		try {
			await props.onSubmit(v);
		} catch (e) {
			setLocalError((e as Error).message);
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<div className="max-w-xl space-y-4">
			{props.showTemplates && (
				<div className="flex flex-wrap gap-1 text-xs">
					<span className="text-neutral-500">Templates:</span>
					{OFFER_TEMPLATES.map((t) => (
						<button
							key={t.id}
							type="button"
							onClick={() => setV({ ...t })}
							className="rounded border border-neutral-300 bg-neutral-50 px-2 py-0.5 hover:bg-neutral-100"
						>
							{t.label}
						</button>
					))}
				</div>
			)}
			<div className="grid gap-3">
				<label className="text-sm">
					Title
					<input
						className="block w-full rounded border px-2 py-1"
						value={v.title}
						onChange={(e) => field("title")(e.target.value)}
					/>
				</label>
				<label className="text-sm">
					Vertical
					<select
						className="block w-full rounded border px-2 py-1"
						value={v.vertical}
						onChange={(e) => field("vertical")(e.target.value)}
					>
						<option value="casino">casino</option>
						<option value="sportsbook">sportsbook</option>
						<option value="poker">poker</option>
						<option value="fintech">fintech</option>
						<option value="crypto">crypto</option>
					</select>
				</label>
				<label className="text-sm">
					Payout type
					<select
						className="block w-full rounded border px-2 py-1"
						value={v.payoutType}
						onChange={(e) => field("payoutType")(e.target.value as OfferFormValues["payoutType"])}
					>
						<option value="cpa">cpa</option>
						<option value="cpl">cpl</option>
						<option value="revshare">revshare</option>
					</select>
				</label>
				<label className="text-sm">
					Payout amount (USD)
					<input
						className="block w-full rounded border px-2 py-1"
						value={v.payoutAmount}
						onChange={(e) => field("payoutAmount")(e.target.value)}
					/>
				</label>
				<label className="text-sm">
					Geos (comma-separated ISO-2)
					<input
						className="block w-full rounded border px-2 py-1"
						value={v.geos}
						onChange={(e) => field("geos")(e.target.value)}
					/>
				</label>
				<label className="text-sm">
					Min monthly volume
					<input
						className="block w-full rounded border px-2 py-1"
						value={v.minVolume}
						onChange={(e) => field("minVolume")(e.target.value)}
					/>
				</label>
			</div>
			{err && <div className="text-sm text-red-700">{err}</div>}
			<div className="flex gap-2">
				<button
					type="button"
					onClick={submit}
					disabled={submitting || !v.title}
					className="rounded bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
				>
					{props.submitLabel}
				</button>
				<button
					type="button"
					onClick={props.onCancel}
					className="rounded bg-neutral-200 px-3 py-1 text-sm hover:bg-neutral-300"
				>
					Cancel
				</button>
			</div>
		</div>
	);
}

export async function createOffer(v: OfferFormValues): Promise<{ id: string }> {
	return api.post<{ id: string }>("/offers", offerPayload(v));
}

export async function updateOffer(id: string, v: OfferFormValues): Promise<void> {
	await api.patch(`/offers/${id}`, offerPayload(v));
}
